import { BaseService } from './base';

// Define ApiResponse type for consistency
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
}

export interface Transformation {
  type: 'background_removal' | 'resize' | 'format_conversion' | 'ai_upscale' | 'color_adjustment' | 'quality_enhancement' | 'auto_enhancement' | 'sharpen' | 'blur' | 'generative_fill' | 'generative_remove' | 'generative_replace' | 'generative_background_replace' | 'auto_tagging' | 'object_detection' | 'invisible_mannequin' | 'object_placement' | 'invisible_mannequin_pro' | 'object_placement_pro' | 'product_photo_studio' | 'jewelry_photo_studio';
  // Resize properties
  width?: number;
  height?: number;
  mode?: 'scale' | 'fit' | 'fill' | 'crop' | 'pad' | 'limit' | 'lfill' | 'lpad' | 'mfit' | 'mpad' | 'thumb' | 'auto' | 'auto_pad' | 'fill_pad';
  gravity?: 'center' | 'north' | 'south' | 'east' | 'west' | 'north_east' | 'north_west' | 'south_east' | 'south_west' | 'face' | 'faces' | 'auto' | 'auto:subject' | 'auto:face';
  
  // Format conversion properties
  format?: 'jpg' | 'jpeg' | 'png' | 'webp' | 'avif' | 'auto';
  quality?: number;
  
  // AI upscale properties
  scale?: 2 | 4;
  
  // Color adjustment properties
  saturation?: number; // -100 to 100
  brightness?: number; // -99 to 100
  contrast?: number;   // -100 to 100
  hue?: number;        // -100 to 100
  vibrance?: number;   // -100 to 100
  
  // Background removal properties
  backgroundColor?: string; // Color for padding transparent areas
  
  // Sharpen/Blur properties
  strength?: number; // 1-2000 for sharpen/blur effects
  
  // AI Generative properties
  prompt?: string; // For generative transformations (fill, remove, background replace)
  from?: string; // For generative replace - object to replace
  to?: string; // For generative replace - replacement description
  
  // Object placement properties
  product?: string; // For object placement - product description
  environment?: string; // For object placement - environment description
  focus?: 'hero' | 'contextual'; // For object placement - how to present the product
  lighting?: 'bright_day' | 'soft_morning' | 'golden_hour' | 'overcast' | 'evening' | 'night' | 'studio'; // For object placement - lighting condition
  
  // AI Analysis properties
  confidence?: number; // For auto-tagging (0.1 to 1.0)
  objects?: string; // For object detection - specific objects to detect
  
  // Invisible mannequin BFL properties
  seed?: number; // For consistent results across batch
  guidance_scale?: number; // How closely to follow the prompt (1-20, default 10)
  steps?: number; // Processing steps (20-100, default 50)

  // Product Photo Studio properties
  outputSize?: 'reels' | 'posts' | 'marketplace' | 'custom'; // Size preset
  customWidth?: number; // Custom width (100-4000)
  customHeight?: number; // Custom height (100-4000)
  colorSensitiveMode?: boolean; // Enable color preservation for jewelry/gems
  keepOriginalFraming?: boolean; // Preserve exact view - don't complete/extend product
}

export interface Job {
  id: string;
  batch_id: string;
  user_id: string;
  store_id: string;
  product_id: string;
  original_image_url: string;
  processed_image_url: string;
  transformations: Transformation[];
  cloudinary_parameters: string;
  processing_metadata?: any;
  transformation_credits: number;
  status: 'pending' | 'processing' | 'processed_pending_approval' | 'approved' | 'rejected' | 'uploading_to_magento' | 'complete' | 'failed' | 'cancelled';
  retry_count: number;
  max_retries: number;
  error_message?: string;
  final_magento_url?: string;
  magento_file_path?: string;
  approved_at?: string;
  approved_by?: string;
  processed_at?: string;
  magento_updated_at?: string;
  last_retry_at?: string;
  billable_events?: any[];
  created_at: string;
  started_at?: string;
  completed_at?: string;
  updated_at: string;
  products: {
    id: string;
    name: string;
    sku: string;
    magento_id: string;
  };
  stores: {
    id: string;
    name: string;
  };
}

export interface BatchJobRequest {
  product_ids: string[];
  transformations: Transformation[];
  store_id: string;
  selected_images?: string[]; // Optional: for individual product processing with specific images
  image_type?: 'all' | 'main' | 'gallery'; // Optional: for bulk processing to filter image types
}

export interface BatchJobResponse {
  success: boolean;
  message?: string;
  data: {
    batch_id?: string;
    total_jobs?: number;
    products_count?: number;
    transformations_applied?: number;
    // Async job properties
    job_id?: string;
    estimated_time?: string;
    product_count?: number;
    async?: boolean;
  };
}

export interface AsyncJobStatus {
  success: boolean;
  data: {
    job_id: string;
    status: string;
    progress?: {
      current?: number;
      total?: number;
      status?: string;
      created_jobs?: number;
      failed_count?: number;
      progress?: number;
    };
    result?: {
      status: string;
      total_products: number;
      created_jobs: number;
      failed_products: string[];
      success_rate: number;
      batch_ids?: string[];  // Array of batch IDs for created jobs
    };
    error?: string;
  };
}

export interface JobsListResponse {
  success: boolean;
  data: {
    jobs: Job[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    };
  };
}

export interface JobResponse {
  success: boolean;
  data: Job;
}

export interface BatchStatus {
  batch_id: string;
  batch_status: string;
  total_jobs: number;
  status_breakdown: {
    pending: number;
    processing: number;
    processed_pending_approval: number;
    approved: number;
    rejected: number;
    uploading_to_magento: number;
    complete: number;
    failed: number;
    cancelled: number;
  };
  progress: {
    percentage: number;
    completed: number;
    remaining: number;
  };
  created_at: string;
  reviewed_at?: string;
}

export interface BatchStatusResponse {
  success: boolean;
  data: BatchStatus;
}

export interface TransformationPreset {
  name: string;
  description: string;
  transformations: Transformation[];
  plan?: string; // All plans have access to all presets
  estimatedCredits?: number;
  processingTime?: string;
  icon?: string;
  usageInfo?: boolean; // Flag to show usage calculation display
  costInfo?: {
    credits: number;
    dollarCost: number;
    description: string;
  };
  requiresInput?: boolean;
  inputFields?: Array<{
    name: string;
    label: string;
    type?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    options?: string;
    defaultValue?: string;
  }>;
}

export interface TransformationPresetsResponse {
  success: boolean;
  data: Record<string, TransformationPreset>;
  user_plan?: string;
  total_presets?: number;
}

export interface JobsQueryParams {
  status?: string;
  store_id?: string;
  batch_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface JobStatusUpdateRequest {
  status: Job['status'];
  error_message?: string;
  processed_image_url?: string;
}

class JobService extends BaseService {
  private readonly endpoint = '/jobs';

  // Create batch jobs for selected products
  async createBatchJobs(request: BatchJobRequest): Promise<BatchJobResponse> {
    // Use async endpoint for large batches
    const endpoint = request.product_ids.length > 100 ? `${this.endpoint}/batch-async` : `${this.endpoint}/batch`;
    
    // Clean up transformations - remove prompt from invisible_mannequin
    const cleanedRequest = {
      ...request,
      transformations: request.transformations.map(t => {
        if (t.type === 'invisible_mannequin') {
          // Remove prompt field as it's handled by the BFL service
          const { prompt, ...cleanTransform } = t as any;
          return cleanTransform;
        }
        return t;
      })
    };
    
    // makeRequest extracts the data field, so we need to type it correctly
    const data = await this.makeRequest<BatchJobResponse['data']>(endpoint, {
      method: 'POST',
      body: JSON.stringify(cleanedRequest),
    });
    
    // Wrap it back in the expected response format
    return {
      success: true,
      data
    };
  }

  // Get async job status
  async getAsyncJobStatus(jobId: string): Promise<AsyncJobStatus> {
    const data = await this.makeRequest<AsyncJobStatus['data']>(`${this.endpoint}/async-status/${jobId}`);
    return {
      success: true,
      data
    };
  }

  // Get jobs list with filtering and pagination
  async getJobs(params: JobsQueryParams = {}): Promise<JobsListResponse> {
    const searchParams = new URLSearchParams();

    if (params.status) searchParams.append('status', params.status);
    if (params.store_id) searchParams.append('store_id', params.store_id);
    if (params.batch_id) searchParams.append('batch_id', params.batch_id);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());
    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    if (params.sort_order) searchParams.append('sort_order', params.sort_order);

    const url = searchParams.toString()
      ? `${this.endpoint}?${searchParams.toString()}`
      : this.endpoint;

    // Use makeFullRequest to get pagination data
    const response = await this.makeFullRequest<Job[]>(url);
    
    return {
      success: true,
      data: {
        jobs: response.data || [],
        pagination: {
          total: response.pagination?.total || 0,
          limit: response.pagination?.limit || 50,
          offset: ((response.pagination?.page || 1) - 1) * (response.pagination?.limit || 50),
          has_more: response.pagination?.hasNext || false
        }
      }
    };
  }

  // Get single job details
  async getJob(id: string): Promise<JobResponse> {
    const job = await this.makeRequest<Job>(`${this.endpoint}/${id}`);
    return {
      success: true,
      data: job
    };
  }

  // Update job status (typically used by processing system)
  async updateJobStatus(id: string, request: JobStatusUpdateRequest): Promise<{ success: boolean; message: string }> {
    const response = await this.makeRequest<{ success: boolean; message: string }>(`${this.endpoint}/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
    return response;
  }

  // Cancel a job
  async cancelJob(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.makeRequest<{ success: boolean; message: string }>(`${this.endpoint}/${id}/cancel`, {
      method: 'PATCH',
    });
    return response;
  }

  // Get batch status and summary
  async getBatchStatus(batchId: string): Promise<BatchStatusResponse> {
    const data = await this.makeRequest<BatchStatus>(`${this.endpoint}/batch/${batchId}`);
    return {
      success: true,
      data
    };
  }

  // Get transformation presets
  async getTransformationPresets(): Promise<TransformationPresetsResponse> {
    const response = await this.makeRequest<{ presets: Record<string, TransformationPreset>; user_plan?: string; total_presets?: number }>(`${this.endpoint}/transformations/presets`);
    return {
      success: true,
      data: response.presets,
      user_plan: response.user_plan,
      total_presets: response.total_presets
    };
  }

  // Get transformation comparison
  async getTransformationComparison(): Promise<{ success: boolean; data: any }> {
    const response = await this.makeRequest<any>(`${this.endpoint}/transformations/comparison`);
    return {
      success: true,
      data: response
    };
  }

  // Get jobs pending approval
  async getJobsPendingApproval(params: JobsQueryParams = {}): Promise<JobsListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.store_id) searchParams.append('store_id', params.store_id);
    if (params.batch_id) searchParams.append('batch_id', params.batch_id);
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());

    const url = searchParams.toString() 
      ? `${this.endpoint}/pending-approval?${searchParams.toString()}`
      : `${this.endpoint}/pending-approval`;

    // This endpoint returns { jobs: [...], pagination: {...} } in data field
    const response = await this.makeRequest<{ jobs: Job[]; pagination: any }>(`${url}`);
    return {
      success: true,
      data: {
        jobs: response.jobs || [],
        pagination: {
          total: response.pagination?.total || 0,
          limit: response.pagination?.limit || 50,
          offset: ((response.pagination?.page || 1) - 1) * (response.pagination?.limit || 50),
          has_more: response.pagination?.hasNext || false
        }
      }
    };
  }

  // Approve individual job
  async approveJob(id: string): Promise<{ success: boolean; message: string; data: { job_id: string; status: string } }> {
    const data = await this.makeRequest<{ job_id: string; status: string }>(`${this.endpoint}/${id}/approve`, {
      method: 'POST',
    });
    
    // makeRequest extracts data, so we need to wrap it back
    return {
      success: true,
      message: 'Job approved successfully',
      data
    };
  }

  // Reject individual job
  async rejectJob(id: string, reason?: string): Promise<{ success: boolean; message: string; data: { job_id: string; status: string } }> {
    const data = await this.makeRequest<{ job_id: string; status: string }>(`${this.endpoint}/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejection_reason: reason }),
    });
    
    // makeRequest extracts data, so we need to wrap it back
    return {
      success: true,
      message: 'Job rejected successfully',
      data
    };
  }

  // Bulk approve jobs (can be from multiple batches)
  async bulkApproveJobs(jobIds: string[]): Promise<{ 
    success: boolean; 
    message: string; 
    data: { 
      approved_count: number; 
      job_ids: string[] 
    } 
  }> {
    const data = await this.makeRequest<{ 
      approved_count: number; 
      job_ids: string[] 
    }>(`${this.endpoint}/bulk-approve`, {
      method: 'POST',
      body: JSON.stringify({ job_ids: jobIds }),
    });
    
    // makeRequest extracts data, so we need to wrap it back
    return {
      success: true,
      message: `Successfully approved ${data.approved_count} jobs`,
      data
    };
  }

  // Get cost estimation for transformations
  async estimateCost(transformations: Transformation[], productCount: number = 1, imageCount?: number, productIds?: string[]): Promise<{
    success: boolean;
    data: {
      credits_per_image: number;
      total_credits: number;
      image_count: number;
      product_count: number;
      breakdown: Array<{ type: string; credits_per_image: number; total_credits: number; description: string }>;
      summary: { ai_transformations: number; basic_transformations: number };
      calculation_method?: 'provided' | 'database_lookup' | 'estimated';
    };
  }> {
    const requestBody: any = { transformations, product_count: productCount };
    if (imageCount !== undefined) {
      requestBody.image_count = imageCount;
    }
    if (productIds && productIds.length > 0) {
      requestBody.product_ids = productIds;
    }

    const response = await this.makeRequest<{
      success: boolean;
      data: {
        credits_per_image: number;
        total_credits: number;
        image_count: number;
        product_count: number;
        breakdown: Array<{ type: string; credits_per_image: number; total_credits: number; description: string }>;
        summary: { ai_transformations: number; basic_transformations: number };
        calculation_method?: 'provided' | 'database_lookup' | 'estimated';
      };
    }>(`${this.endpoint}/estimate-cost`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
    return response;
  }

  // Test Cloudinary configuration
  async testCloudinaryConfig(): Promise<{
    success: boolean;
    data: {
      success: boolean;
      cloudName?: string;
      baseUrl?: string;
      testUrl?: string;
      exampleUrl?: string;
      message: string;
      error?: string;
    };
  }> {
    const response = await this.makeRequest<{
      success: boolean;
      data: {
        success: boolean;
        cloudName?: string;
        baseUrl?: string;
        testUrl?: string;
        exampleUrl?: string;
        message: string;
        error?: string;
      };
    }>(`${this.endpoint}/cloudinary/test`);
    return response;
  }

  // Helper method to calculate estimated cost for transformations
  // This now uses the backend API for consistency with real image counts
  async calculateEstimatedCostAsync(transformations: Transformation[], imageCount: number, productIds?: string[]): Promise<number> {
    try {
      const response = await this.estimateCost(transformations, 1, imageCount, productIds);
      return response.data.total_credits;
    } catch (error) {
      console.error('Failed to calculate estimated cost via API, falling back to local calculation:', error);
      return this.calculateEstimatedCostLocal(transformations, imageCount);
    }
  }

  // Local fallback calculation (kept for offline scenarios)
  private calculateEstimatedCostLocal(transformations: Transformation[], imageCount: number): number {
    let totalCreditsPerImage = 0;
    
    for (const transformation of transformations) {
      switch (transformation.type) {
        case 'background_removal':
          totalCreditsPerImage += 1.0;
          break;
        case 'ai_upscale':
          totalCreditsPerImage += transformation.scale === 4 ? 3.0 : 2.0;
          break;
        case 'generative_fill':
          totalCreditsPerImage += 2.5;
          break;
        case 'generative_remove':
          totalCreditsPerImage += 2.0;
          break;
        case 'generative_replace':
          totalCreditsPerImage += 3.0;
          break;
        case 'generative_background_replace':
          totalCreditsPerImage += 3.5;
          break;
        case 'invisible_mannequin':
          totalCreditsPerImage += 8.0; // BFL FLUX Kontext API - $0.08 per image
          break;
        case 'object_placement':
          totalCreditsPerImage += 12.0; // BFL FLUX Kontext API - virtual staging/placement
          break;
        case 'invisible_mannequin_pro':
          totalCreditsPerImage += 16.0; // Gemini API - $0.16 per image (2x cost)
          break;
        case 'object_placement_pro':
          totalCreditsPerImage += 24.0; // Gemini API - $0.24 per image (2x cost)
          break;
        case 'auto_tagging':
          totalCreditsPerImage += 0.5;
          break;
        case 'object_detection':
          totalCreditsPerImage += 0.5;
          break;
        case 'quality_enhancement':
          totalCreditsPerImage += 1.5;
          break;
        case 'auto_enhancement':
          totalCreditsPerImage += 1.0;
          break;
        case 'resize':
        case 'format_conversion':
        case 'color_adjustment':
        case 'sharpen':
        case 'blur':
          totalCreditsPerImage += 0.1;
          break;
        default:
          totalCreditsPerImage += 0.1;
      }
    }

    return Number((imageCount * Math.max(0.1, totalCreditsPerImage)).toFixed(1));
  }

  // Synchronous version for backward compatibility
  calculateEstimatedCost(transformations: Transformation[], imageCount: number): number {
    return this.calculateEstimatedCostLocal(transformations, imageCount);
  }

  // Get active jobs status only
  async getActiveJobsStatus(): Promise<ApiResponse<{
    activeJobs: Array<{ id: string; status: string; updated_at: string }>;
    count: number;
    hasActiveJobs: boolean;
  }>> {
    const data = await this.makeRequest<{
      activeJobs: Array<{ id: string; status: string; updated_at: string }>;
      count: number;
      hasActiveJobs: boolean;
    }>(`${this.endpoint}/active-status`);
    
    return {
      success: true,
      data
    };
  }

  // Get job statistics by status
  async getJobStats(): Promise<{
    success: boolean;
    data: {
      total: number;
      pending: number;
      processing: number;
      processed_pending_approval: number;
      approved: number;
      rejected: number;
      uploading_to_magento: number;
      complete: number;
      failed: number;
      cancelled: number;
    };
  }> {
    const response = await this.makeRequest<{
      total: number;
      pending: number;
      processing: number;
      processed_pending_approval: number;
      approved: number;
      rejected: number;
      uploading_to_magento: number;
      complete: number;
      failed: number;
      cancelled: number;
    }>(`${this.endpoint}/stats`);
    
    // BaseService already extracted the data, so wrap it back for JobsPage
    return {
      success: true,
      data: response
    };
  }

  // Retry failed Magento upload
  async retryMagentoUpload(id: string): Promise<{ success: boolean; message: string; data: { job_id: string; status: string; retry_count: number } }> {
    const data = await this.makeRequest<{ job_id: string; status: string; retry_count: number }>(`${this.endpoint}/${id}/retry-upload`, {
      method: 'POST',
    });
    
    return {
      success: true,
      message: 'Upload retry initiated successfully',
      data
    };
  }

  // Helper method to get status display information
  getStatusDisplay(status: Job['status'] | string): { label: string; color: string; icon: string } {
    switch (status) {
      case 'pending':
        return { label: 'Pending', color: 'text-muted-foreground', icon: 'Clock' };
      case 'processing':
        return { label: 'Processing', color: 'text-primary', icon: 'Loader2' };
      case 'processed_pending_approval':
        return { label: 'Pending Approval', color: 'text-warning', icon: 'AlertCircle' };
      case 'approved':
        return { label: 'Approved', color: 'text-success', icon: 'CheckCircle' };
      case 'rejected':
        return { label: 'Rejected', color: 'text-destructive', icon: 'XCircle' };
      case 'uploading_to_magento':
        return { label: 'Uploading', color: 'text-primary', icon: 'Upload' };
      case 'complete':
        return { label: 'Complete', color: 'text-success', icon: 'CheckCircle2' };
      case 'failed':
        return { label: 'Failed', color: 'text-destructive', icon: 'XCircle' };
      case 'cancelled':
        return { label: 'Cancelled', color: 'text-muted-foreground', icon: 'X' };
      default:
        // Try to display the status as-is with proper formatting
        const formattedLabel = status
          .replace(/_/g, ' ')
          .split(' ')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        return { label: formattedLabel || 'Unknown', color: 'text-muted-foreground', icon: 'HelpCircle' };
    }
  }

  // Helper method to format transformation for display
  formatTransformation(transformation: any): string {
    // Handle legacy format where transformation properties are at root level
    if (!transformation.type) {
      const parts = [];
      
      // Check for resize
      if (transformation.resize) {
        const resize = transformation.resize;
        const mode = resize.mode || 'fit';
        const gravity = resize.gravity ? ` (${resize.gravity})` : '';
        parts.push(`Resize to ${resize.width}x${resize.height} (${mode}${gravity})`);
      }
      
      // Check for format
      if (transformation.format) {
        const quality = transformation.quality ? ` (${transformation.quality}% quality)` : '';
        parts.push(`Convert to ${transformation.format.toUpperCase()}${quality}`);
      }
      
      // Check for quality alone
      if (transformation.quality && !transformation.format) {
        parts.push(`Quality: ${transformation.quality}%`);
      }
      
      // Check for other properties
      if (transformation.background_removal) {
        parts.push('Remove Background');
      }
      
      if (transformation.ai_upscale) {
        parts.push(`AI Upscale ${transformation.ai_upscale.scale || 2}x`);
      }
      
      return parts.length > 0 ? parts.join(', ') : 'Custom Transformation';
    }

    // Handle standard format with type property
    switch (transformation.type) {
      case 'background_removal':
        const bgColor = transformation.backgroundColor || 'white';
        return `Remove Background${bgColor !== 'transparent' ? ` (${bgColor} bg)` : ''}`;
      case 'resize':
        const mode = transformation.mode || 'fit';
        const gravity = transformation.gravity ? ` (${transformation.gravity})` : '';
        return `Resize to ${transformation.width}x${transformation.height} (${mode}${gravity})`;
      case 'format_conversion':
        const format = transformation.format || 'auto';
        const quality = transformation.quality ? ` (${transformation.quality}% quality)` : '';
        return `Convert to ${format.toUpperCase()}${quality}`;
      case 'ai_upscale':
        return `AI Upscale ${transformation.scale || 2}x`;
      case 'color_adjustment':
        const adjustments = [];
        if (transformation.brightness !== undefined) adjustments.push(`brightness ${transformation.brightness > 0 ? '+' : ''}${transformation.brightness}`);
        if (transformation.saturation !== undefined) adjustments.push(`saturation ${transformation.saturation > 0 ? '+' : ''}${transformation.saturation}`);
        if (transformation.contrast !== undefined) adjustments.push(`contrast ${transformation.contrast > 0 ? '+' : ''}${transformation.contrast}`);
        if (transformation.hue !== undefined) adjustments.push(`hue ${transformation.hue > 0 ? '+' : ''}${transformation.hue}`);
        if (transformation.vibrance !== undefined) adjustments.push(`vibrance ${transformation.vibrance > 0 ? '+' : ''}${transformation.vibrance}`);
        return adjustments.length > 0 ? `Color: ${adjustments.join(', ')}` : 'Color Adjustment';
      case 'quality_enhancement':
        return 'AI Quality Enhancement';
      case 'auto_enhancement':
        return 'Auto Enhancement';
      case 'sharpen':
        return `Sharpen${transformation.strength ? ` (${transformation.strength})` : ''}`;
      case 'blur':
        return `Blur${transformation.strength ? ` (${transformation.strength})` : ''}`;
      case 'generative_fill':
        return `Generative Fill (${transformation.width}x${transformation.height})`;
      case 'generative_remove':
        return `Generative Remove: ${transformation.prompt || 'objects'}`;
      case 'generative_replace':
        return `Replace "${transformation.from || 'object'}" with "${transformation.to || 'replacement'}"`;
      case 'generative_background_replace':
        return `Replace Background: ${transformation.prompt || 'new background'}`;
      case 'invisible_mannequin':
        return 'Invisible Mannequin (Fashion)';
      case 'object_placement':
        const focusText = transformation.focus === 'contextual' ? '(Real-life)' : '(Hero)';
        const lightingText = transformation.lighting ? ` - ${transformation.lighting.replace(/_/g, ' ')}` : '';
        return `Virtual Staging ${focusText}: ${transformation.product || 'product'} in ${transformation.environment || 'environment'}${lightingText}`;
      case 'auto_tagging':
        const confidenceLevel = transformation.confidence ? ` (${Math.round(transformation.confidence * 100)}% confidence)` : '';
        return `Auto Tagging${confidenceLevel}`;
      case 'object_detection':
        const objects = transformation.objects ? ` (${transformation.objects})` : '';
        return `Object Detection${objects}`;
      default:
        return transformation.type ? (transformation.type as string).replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'Transformation';
    }
  }

  // Get environment presets for virtual staging
  async getEnvironmentPresets(): Promise<any> {
    return this.makeRequest(`${this.endpoint}/transformations/environment-presets`);
  }

  // Remove background from a single image (used by canvas preview)
  async removeBackground(imageUrl: string): Promise<{ url: string; public_id: string; width: number; height: number }> {
    return this.makeRequest(`${this.endpoint}/remove-bg`, {
      method: 'POST',
      body: JSON.stringify({ url: imageUrl }),
    });
  }
}

export const jobService = new JobService();